import { createSelector } from '@reduxjs/toolkit';

import type { Candidate, ExpertRequest } from '@/expertrequest';
import type { RootState } from '@/store';

interface ExpertRequestOwnProps {
  expertRequestId: string;
}

const selectNode = (state: RootState, ownProps: ExpertRequestOwnProps) =>
  state.expertRequests.default.edges.find((e: any) => e.node.id === ownProps.expertRequestId)
    ?.node as Record<string, any> | undefined;

/**
 * True only for a node the detail fetch actually populated.
 *
 * Presence is not enough, which is the whole point (ENG-5721). `UI__SET_USER_CONTEXT`
 * empties every `expertRequests` collection — see the `return initialState` in
 * `store/reducer.ts` — and a candidate fetch resolving afterwards re-creates the node from
 * `expertRequestReducer({ id: expertRequestId }, action)`: an id and some candidate arrays,
 * nothing else. That stub is truthy, so a `!node` check waves it through and the page then
 * dies on the first field the stub does not carry (WEBAPP-CDV / CDW / CDK, ENG-5787).
 *
 * `html_url` is the marker because it is `String!` in the schema and the detail query
 * selects it, so every genuinely loaded node has one and no stub ever does. `name` would
 * not do — it is nullable.
 */
export function isLoadedExpertRequest(node: Record<string, any> | undefined): boolean {
  return typeof node?.html_url === 'string';
}

/**
 * ENG-5538: the ER detail page reads the expert request from exactly one place — the Redux
 * node.
 *
 * It used to also take a frozen `expertRequest` snapshot prop from the route loader and
 * merge an allowlist of "fields a save refreshes" over it on every render (ENG-5292 /
 * ENG-5453). Both sides came from the same `fetchExpertRequest` response — that thunk
 * dispatches EXPERT_REQUEST__UPDATE and returns the very same object the loader passes down
 * — so the snapshot was a redundant copy of what the store already had, and re-merging it
 * minted a new object on every dispatch. That is what re-fired the Request Call dialog's
 * group fetch until React hit its nested-update limit (WEBAPP-CCA / ENG-5536).
 *
 * Reading the node directly also retires the allowlist: EXPERT_REQUEST__UPDATE merges rather
 * than replaces, so a save or lifecycle transition shows through with no reload and nothing
 * has to be added to a list of field names when a new field becomes savable.
 *
 * Memoized because `connect` re-runs this on every dispatch and consumers key effects and
 * form `initialValues` on prop identity. The result is shared across renders — treat it as
 * immutable, the arrays on it are the Redux node's own.
 */
export const selectExpertRequestProps = createSelector(
  [(state: RootState) => state.viewer, selectNode],
  (viewer, node) => ({
    viewer,
    // Undefined until the detail fetch has populated the node. The route loader awaits
    // fetchExpertRequest, so this holds on first paint — but a context switch empties the
    // collection underneath a mounted page, so the page has to handle its absence rather
    // than assume it away (ENG-5721; the old `as ExpertRequest` cast is what let the
    // assumption compile).
    expertRequest: isLoadedExpertRequest(node) ? (node as ExpertRequest) : undefined,
    matchedCandidates: (node?.matchedCandidates || []) as Candidate[],
    suggestedCandidates: (node?.suggestedCandidates || []) as Candidate[],
    // ENG-5537: the reducer stores these as booleans, so the old `|| []` coercion left the
    // candidate spinner and the loaded guard dead, and minted a fresh array on every call.
    loadingCandidates: !!node?.loadingCandidates,
    loadedCandidates: !!node?.loadedCandidates,
    suggestedCandidatesPageInfo: node?.suggestedCandidatesPageInfo,
    matchedCandidatesPageInfo: node?.matchedCandidatesPageInfo,
  })
);
